import {
  DEPOSIT_LIKE_TYPES,
  getBonusUiGroup,
  getFreespinUiGroup,
  UI_GROUP,
} from '../shared/bonusFeedUtils';
import { resolveBonusImageUrl, resolveBonusPlaceholderSrc } from '../shared/bonusImage';
import {
  resolveBonusInstanceDisplayStatus,
  resolveBonusStatus,
  resolveBonusType,
  resolveDisplayCurrency,
  resolveFinanceForCurrency,
  resolveFinishedDate,
  resolveFinishedExpiryDate,
} from '../shared/bonusProps';
import { getBonusCountdownEndMs, getFreespinCountdownEndMs } from '../shared/bonusTimeFormat';

// FreeSpinSubType.DEPOSIT from @atlas/bonuses, inlined so the builder unit tests
// don't have to load the SDK barrel (it pulls @atlas/dto-bonus-service).
const FREESPIN_DEPOSIT_SUBTYPE = 'DEPOSIT';

/**
 * Resolves the bonus detail header timer into:
 *   endMs        - epoch ms deadline for the live <Timer> (active states), else null
 *   finishedDate - static "went Expired" / finished date string (finished states)
 * Exactly one is meaningful at a time, so the header renders a live <Timer> while
 * active and a plain date once finished (FT-8580). Freespins carry FreeSpinStatus
 * values, so the group / deadline must come from the freespin-aware helpers -
 * getBonusUiGroup would misread FreeSpinStatus.ACTIVATED as FINISHED.
 */
export const resolveBonusTimer = (entity, isFreeSpin = false) => {
  const uiGroup = isFreeSpin ? getFreespinUiGroup(entity) : getBonusUiGroup(entity);

  if (uiGroup === UI_GROUP.FINISHED) {
    return {
      endMs: null,
      finishedDate: resolveFinishedExpiryDate(entity) || resolveFinishedDate(entity, ''),
    };
  }

  const endMs = isFreeSpin
    ? getFreespinCountdownEndMs(entity)
    : getBonusCountdownEndMs(entity, uiGroup);

  return { endMs: endMs ?? null, finishedDate: '' };
};

/**
 * Wagering progress block for the detail page bar. Mirrors the bonus card
 * (see resolveWageringProps in shared/bonusProps.js):
 *   multiplier    -> bonus.wager (fallback to copied settings)
 *   totalAmount   -> requiredWagering (bar max)
 *   wageredAmount -> wagering (bar value)
 * The component hides itself when totalAmount <= 0 (templates / non-activated).
 */
const resolveWager = (bonus, currency) => ({
  currency,
  multiplier: bonus.wager || bonus.copiedBonusSettings?.wager || 0,
  totalAmount: bonus.requiredWagering ?? 0,
  wageredAmount: bonus.wagering ?? 0,
});

const COUNTRY_TRANSLATION_PREFIX = 'country_';

/**
 * Localizes a single ISO country code (e.g. "IT") through the shared
 * `country_<iso2>` translation keys — the same convention used by PhoneField.
 * Falls back to the raw code when no translation (or translator) is available
 * so the value is never blank.
 */
const resolveCountryName = (code, t) => {
  if (!t) return code;

  const translated = t(`${COUNTRY_TRANSLATION_PREFIX}${String(code).toLowerCase()}`);

  return !translated || translated.startsWith('#') ? code : translated;
};

/**
 * Builds an id (as string) -> name lookup from the payment methods list.
 */
const buildPaymentMethodNames = (paymentMethods = []) =>
  paymentMethods.reduce((acc, { id, name }) => {
    acc[String(id)] = name;

    return acc;
  }, {});

/**
 * Maps the bonus "not allowed" countries to their localized names.
 * Non-array input is passed through so empty/missing lists still drop the row.
 */
export const resolveNotAllowedCountries = (codes, t) =>
  Array.isArray(codes) ? codes.map(code => resolveCountryName(code, t)) : codes;

/**
 * Maps the bonus "not allowed" payment method ids to their display names,
 * dropping any id that has no matching payment method. Non-array input is
 * passed through so empty/missing lists still drop the row.
 */
export const resolveNotAllowedPaymentMethods = (ids, paymentMethods) => {
  if (!Array.isArray(ids)) return ids;

  const paymentMethodNames = buildPaymentMethodNames(paymentMethods);

  return ids.map(id => paymentMethodNames[String(id)]).filter(name => name != null);
};

export const bonusBuilder = ({ bonus, paymentMethods, t, wallet }) => {
  const type = resolveBonusType(bonus);

  const financeList = resolveFinanceForCurrency(bonus, wallet?.currency);
  const currency = resolveDisplayCurrency(financeList, bonus, wallet);
  const resolvedBonusImage = resolveBonusImageUrl(bonus.imageLink);
  const resolvedBonusImagePlaceholder = resolveBonusPlaceholderSrc({
    bonusType: type,
    isFreeSpin: false,
  });

  const imageUrl = resolvedBonusImage || resolvedBonusImagePlaceholder;
  const status = resolveBonusInstanceDisplayStatus(bonus);
  const gameIds = bonus.gameIds || [];
  const gamesCount = bonus.gameIds?.length ?? 0;

  const settings = {
    ...bonus,
    ...(bonus.copiedBonusSettings || {}),
    ...(bonus?.sportLimit || {}),
  };

  return {
    additionalTerms: {
      allowedSportServices: settings.allowedSportServices,
      allowedSports: settings.allowedSports,
      maxBetAmount: financeList.maxBetAmount,
      maxBonusAmount: financeList.maxBonusAmount,
      maxWagerAmount: bonus.maxWagerAmount,
      minBetAmount: financeList.minBetAmount,
      minCountOfBets: settings.requiredBetsToWager,
      minEventsCountMultiple: settings.multipleEventOddsLimit?.min,
      minOddsLimitSingle: settings.singleOddsLimit?.min,
      minTotalOddsMultiple: settings.multipleOddsLimit?.min,
      minWagerAmount: bonus.minWagerAmount,
      notAllowedBetTypes: settings.notAllowedBetTypes,
      notAllowedCountries: resolveNotAllowedCountries(bonus.notAllowedCountries, t),
      notAllowedPaymentMethods: resolveNotAllowedPaymentMethods(
        bonus.notAllowedPaymentMethods,
        paymentMethods,
      ),
      notAllowedSports: settings.notAllowedSports,
      sportTreeLimitType: settings.sportTreeLimitType,
    },
    bonusInfo: {
      bonusAmount: bonus.bonusAmount || financeList.maxBonusAmount,
      bonusPercent: financeList.maxProfitPercentage,
      bonusReceived: bonus.bonusAmount,
      calculationType: bonus.copiedBonusSettings?.maxBonusCalculationType,
      depositMultiplier: bonus.copiedBonusSettings?.depositMultiplier,
      maxBonusAmount: financeList.maxBonusAmount,
      minDeposit: financeList.minDeposit,
      product: type,
      wager: bonus.wager,
    },
    currency,
    gameIds,
    gamesCount,
    imageUrl,
    isDepositLike: DEPOSIT_LIKE_TYPES.has(bonus.type),
    name: bonus.name,
    nameHighlighted: bonus.nameHighlighted,
    status,
    timer: resolveBonusTimer(bonus),
    type,
    wager: resolveWager(bonus, currency),
  };
};

export const freespinBuilder = ({ bonus, language }) => {
  const type = resolveBonusType(bonus);
  const isDepositLike = bonus.freeSpinSubType === FREESPIN_DEPOSIT_SUBTYPE;

  const resolvedBonusImage = resolveBonusImageUrl(bonus.imageLink);
  const resolvedBonusImagePlaceholder = resolveBonusPlaceholderSrc({
    bonusType: type,
    isFreeSpin: true,
  });

  const imageUrl = resolvedBonusImage || resolvedBonusImagePlaceholder;
  const status = resolveBonusStatus(bonus);
  const gameIds = bonus.freeSpin?.casinoLimit?.gameIds || [];
  const gamesCount = bonus.freeSpin?.casinoLimit?.gameIds?.length ?? 0;

  const localizedInfoList = bonus.freeSpin?.localizedInfoList || [];
  const localizedInfo =
    localizedInfoList.find(info => info.language === language) || localizedInfoList[0];

  return {
    // Max win per the freespin currency. Stays undefined (Additional Terms hides it)
    // until the SDK FreeSpin exposes freeSpinTotalMaxWin (FT-8533).
    additionalTerms: { maxWin: bonus.freeSpinTotalMaxWin?.[bonus.currency] },
    bonusInfo: {
      // FS amount per the freespin currency (freeSpinAmounts[currency]); scalar freeSpinAmount as fallback.
      amount: bonus.freeSpinAmounts?.[bonus.currency] ?? bonus.freeSpinAmount,
      count: bonus.freeSpin?.numberOfSpins,
      // Min deposit only for DEPOSIT freespins (per the freespin currency).
      minDeposit: isDepositLike ? bonus.freeSpinMinAmountsDeposit?.[bonus.currency] : undefined,
    },
    currency: bonus.currency,
    freeSpinSubType: bonus.freeSpinSubType,
    gameIds,
    gamesCount,
    imageUrl,
    isDepositLike,
    name: localizedInfo?.name || bonus.freeSpin?.name,
    nameHighlighted: localizedInfo?.nameHighlighted,
    status,
    timer: resolveBonusTimer(bonus, true),
    type,
  };
};
